import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { Link } from "react-router-dom";
import { ChevronRight, Sun, Activity, ThermometerSun, Zap, CheckCircle2, AlertCircle, Clock, Target, ShieldCheck } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

export default function RedLightTherapy() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Red Light Therapy in Longview, TX | Woods Spine & Wellness"
        description="Discover the clinical benefits of Red Light Therapy for inflammation, pain relief, and Dahlia body contouring at Woods Spine & Wellness in Longview, TX."
        ogImage="https://vibe.filesafe.space/1774569058151009111/attachments/e3fefda6-4541-41e2-b1b1-3631721c72b4.png"
        canonical="https://woodsspine.com/longview-red-light-therapy"
      />
      <Navbar />
      <main className="flex-grow pt-24 pb-16">
        <section className="container mx-auto px-4 mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-full border bg-secondary/50 px-3 py-1 text-sm font-medium text-primary mb-2">
                <Sun className="mr-2 h-4 w-4 text-accent" />
                Cellular Wellness & Contouring
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
                Red Light Therapy <br />
                <span className="text-accent">in Longview, TX</span>
              </h1>
              <p className="text-xl font-medium text-primary/80">Featuring Dahlia Body Contouring & Cellular Wellness Technology</p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Experience the incredible dual benefits of our cutting-edge technology. Whether you're seeking profound clinical pain relief, reduced inflammation, or non-invasive body contouring that burns 500 calories per session, Red Light Therapy offers a transformative approach to your health.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button size="lg" className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">
                    Book Your Session <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-black/5 rounded-3xl transform -rotate-3"></div>
              <div className="bg-black overflow-hidden rounded-3xl shadow-xl relative z-10 border border-border flex items-center justify-center aspect-[4/3]">
                <img 
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/attachments/e3fefda6-4541-41e2-b1b1-3631721c72b4.png&w=1280&output=webp&q=80" 
                  alt="Dahlia Red Light Therapy Bed" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 opacity-90"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        {/* WHAT IT IS */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What is Red Light Therapy?</h2>
            <div className="prose prose-lg text-muted-foreground">
              <p>
                Red Light Therapy (RLT), also known scientifically as photobiomodulation (PBM), is a non-invasive, highly effective clinical treatment that uses specific wavelengths of red and near-infrared light to promote profound cellular health. At Woods Spine & Wellness, we utilize the advanced Dahlia Red Light system, which is expertly designed to deliver powerful, full-body light therapy that addresses both aesthetic and deeply rooted clinical concerns.
              </p>
              <p>
                Unlike ultraviolet (UV) light from the sun or traditional tanning beds, which can damage your skin and cellular DNA, red and near-infrared light are completely safe, natural, and incredibly beneficial for your cells. When these specific, targeted wavelengths of light are absorbed by the body, they pass through the skin and directly stimulate the mitochondria—the critical "powerhouses" of your cells. This stimulation dramatically increases the production of Adenosine Triphosphate (ATP), which is the essential energy currency your body needs to function, heal, and thrive.
              </p>
              <p>
                This massive boost in cellular energy creates a powerful ripple effect throughout the entire body, offering dual benefits. Clinically, it helps cells repair themselves faster, significantly reduces chronic systemic inflammation, improves blood circulation, and accelerates tissue healing for pain relief. Aesthetically, the Dahlia system excels at body contouring by temporarily opening fat cells to release stored lipids (a process called lipolysis), while simultaneously boosting collagen production to tighten skin. Whether you are looking to recover from a sports injury, manage arthritis pain, or reduce stubborn fat, Red Light Therapy offers a relaxing, scientifically-backed solution.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">How Dahlia Red Light Works</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The Dahlia Red Light system uses advanced medical-grade LEDs to deliver precise wavelengths of light deep into your tissues. Here is the step-by-step process of how it facilitates both clinical healing and body contouring:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <Zap className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">1. Cellular Energy Production</h3>
                  <p className="text-muted-foreground">The red and near-infrared light is actively absorbed by a photoreceptor in your cells called cytochrome c oxidase. This interaction boosts the rapid production of Adenosine Triphosphate (ATP), giving your cells the massive energy surge they need to function optimally, fight oxidative stress, and repair tissue damage.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <ThermometerSun className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">2. Fat Reduction (Lipolysis)</h3>
                  <p className="text-muted-foreground">For targeted body contouring, the specific wavelengths of light cause temporary, microscopic pores to form in the membranes of fat cells. This allows the stored fat (lipids) and toxins to safely leak out into the interstitial space, where they are naturally processed, metabolized, and eliminated by the body's lymphatic system.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <Activity className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">3. Reduced Inflammation</h3>
                  <p className="text-muted-foreground">Red Light Therapy has been clinically shown to significantly reduce oxidative stress and lower levels of pro-inflammatory cytokines in the body while increasing blood flow. This makes it an exceptionally effective treatment for managing chronic joint pain, arthritis, neuropathy, and accelerating muscle recovery.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <Sun className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">4. Enhanced Collagen Production</h3>
                  <p className="text-muted-foreground">The therapy directly stimulates fibroblasts, which are the specialized cells responsible for producing collagen and elastin. This leads to dramatically improved skin elasticity, accelerated wound healing, a reduction in fine lines and wrinkles, and a visible decrease in the appearance of cellulite.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-10 text-center">Who is Red Light Therapy For?</h2>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <CheckCircle2 className="h-6 w-6 text-accent mr-2" /> Ideal Candidates
                </h3>
                <p className="text-muted-foreground mb-6">
                  Red Light Therapy is incredibly versatile, gentle, and safe for almost everyone. It is an excellent option for individuals seeking non-invasive, holistic solutions for both clinical health and aesthetic goals:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Stubborn fat reduction and targeted body contouring</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Chronic joint pain, osteoarthritis, and systemic inflammation</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Muscle recovery after intense workouts or sports injuries</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Improving skin health, elasticity, and reducing cellulite</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Neuropathy, nerve pain, and poor circulation</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> General wellness, cellular health, and increased energy levels</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <AlertCircle className="h-6 w-6 text-destructive mr-2" /> Contraindications
                </h3>
                <p className="text-muted-foreground mb-6">
                  While Red Light Therapy is widely considered one of the safest modalities available in modern wellness, there are a few important precautions. You should consult with our doctors before beginning treatment if you:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Are currently pregnant or nursing</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Have active cancer or are currently undergoing cancer treatments</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Suffer from epilepsy or a seizure disorder that is triggered by light</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Are taking specific medications that cause extreme light sensitivity (photosensitivity)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT TO EXPECT */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What to Expect During Your Session</h2>
            <div className="prose prose-lg text-muted-foreground mb-8">
              <p>
                A session in our Dahlia Red Light bed is a completely painless, deeply relaxing experience. It provides the perfect opportunity to unwind, de-stress, and meditate while your body's cells do the heavy lifting of healing and regenerating.
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-8">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Duration</h3>
                  <p className="text-muted-foreground">A typical full-body session lasts just 20 to 30 minutes. Because it is so quick and requires no preparation, you can easily fit a rejuvenating treatment into your lunch break or busy daily routine without missing a beat.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Activity className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">What You'll Feel</h3>
                  <p className="text-muted-foreground">You won't feel any pain, pinching, or discomfort whatsoever. The bed emits a gentle, soothing, and comforting warmth that envelops your body. Many of our patients find the experience so relaxing that they fall into a light sleep during their session.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Recommended Sessions</h3>
                  <p className="text-muted-foreground">For optimal body contouring results or significant chronic pain relief, we generally recommend a protocol of 2 to 3 sessions per week over a 4 to 6 week period. Ongoing maintenance sessions can then be scheduled as needed to sustain your wellness.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <ShieldCheck className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Recovery & Downtime</h3>
                  <p className="text-muted-foreground">There is absolutely zero downtime or recovery period required. You can immediately return to work, exercise, or any other daily activities feeling refreshed, energized, and ready to tackle the rest of your day.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* LOCAL RELEVANCE */}
        <section className="py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Premier Wellness Services in Longview, TX</h2>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              Woods Spine & Wellness is incredibly proud to bring the advanced Dahlia Red Light Therapy system to the Longview, TX community. Whether you are an East Texas resident looking to accelerate your weight loss journey, a local athlete seeking faster recovery between events, or someone desperately looking for natural relief from chronic inflammation and arthritis, our clinic provides the state-of-the-art care you deserve. We are deeply dedicated to helping our neighbors achieve their comprehensive wellness goals in a comfortable, professional environment right here at home.
            </p>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-8">
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is Red Light Therapy safe?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Yes, it is completely safe and backed by thousands of clinical studies. Red Light Therapy does not use harmful UV light, so there is absolutely no risk of sunburn, skin damage, or cellular mutation. It is a non-invasive, FDA-cleared technology with no known negative side effects when used appropriately under professional guidance.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">How long until I see results?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Many patients report feeling significantly more energized, experiencing less joint stiffness, and noticing improved skin tone after just 1 or 2 sessions. For targeted body contouring and fat reduction, measurable results—such as inches lost around the waist or thighs—are typically seen after completing a full, consistent protocol of 8 to 12 sessions over a few weeks.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Do I need to diet and exercise for body contouring to work?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  While Red Light Therapy effectively releases fat from your cells on its own, your body's lymphatic system must actively process and eliminate that released fat. We strongly recommend drinking plenty of water and engaging in light cardiovascular exercise (like brisk walking or using our whole-body vibration plates) immediately after your session to stimulate lymphatic drainage and maximize your contouring results.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is Red Light Therapy covered by insurance?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Because Red Light Therapy is widely considered an elective wellness, anti-aging, and body contouring treatment, it is typically not covered by standard health insurance plans or Medicare. However, we are committed to your health and offer highly affordable packages, new patient specials, and flexible financing options to make this therapy accessible.
                </p>
              </div>
              <div className="pb-2">
                <h3 className="text-xl font-bold text-primary mb-3">What should I wear during the treatment?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  For the therapy to be fully effective, the red and near-infrared light needs to be absorbed directly by your bare skin rather than passing through clothing. We highly recommend wearing undergarments, a two-piece swimsuit, or athletic shorts and a sports bra during your session to expose as much skin as comfortably possible to the healing light.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-secondary/30 text-center border-t border-border">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Ready to Experience the Benefits?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Whether you want to reduce chronic pain, accelerate cellular healing, or contour your body naturally, Red Light Therapy can help. Schedule your session today.
            </p>
            <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
              <Link to="/appointment-scheduling">
                Book Your Session Now <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
